import { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckCircle, ChevronDown, ChevronUp, Clock, ShieldAlert, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/Modal';
import { tabService, type TabNotification } from '@/services/tabs';
import wsService from '@/services/websocket.service';
import { toast } from 'sonner';
import {
  buildSecurityAlertsFromTabs,
  dismissSecurityAlert,
  getSecurityAlertDismissedIds,
  getSecurityAlertReadIds,
  isSecurityAlertId,
  markAllSecurityAlertsRead,
  markSecurityAlertRead,
  type TabForSecurityAlert,
} from '@/components/tabs/securityAlerts';
import { scanTabsSecurity } from '@/components/tabs/tabSecurity';
import { cn } from '@/utils';

const RELATIVE_TIME_UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
  { unit: 'year', seconds: 60 * 60 * 24 * 365 },
  { unit: 'month', seconds: 60 * 60 * 24 * 30 },
  { unit: 'week', seconds: 60 * 60 * 24 * 7 },
  { unit: 'day', seconds: 60 * 60 * 24 },
  { unit: 'hour', seconds: 60 * 60 },
  { unit: 'minute', seconds: 60 },
  { unit: 'second', seconds: 1 },
];

function formatRelative(iso: string): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diffSec = Math.round((ts - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const { unit, seconds } of RELATIVE_TIME_UNITS) {
    if (Math.abs(diffSec) >= seconds || unit === 'second') {
      const value = Math.round(diffSec / seconds);
      return formatter.format(value, unit);
    }
  }
  return '';
}

function typeColor(type: TabNotification['type']) {
  const colors = {
    info: 'bg-info-500/20 text-info-400',
    warning: 'bg-warning-500/20 text-warning-400',
    success: 'bg-success-500/20 text-success-400',
    error: 'bg-error-500/20 text-error-400',
  };
  return colors[type];
}

export interface TabAlertsPanelProps {
  sessionId?: string | null;
  /** When set, security alerts are built from these tabs (no extra fetch). */
  sessionTabs?: TabForSecurityAlert[];
  className?: string;
}

export function TabAlertsPanel({ sessionId, sessionTabs, className }: TabAlertsPanelProps) {
  const [notifications, setNotifications] = useState<TabNotification[]>([]);
  const [securityAlerts, setSecurityAlerts] = useState<TabNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
  const [readRevision, setReadRevision] = useState(0);

  const buildAlertsFromScans = useCallback(async (tabs: TabForSecurityAlert[]) => {
    const active = tabs.filter((t) => t.url);
    if (active.length === 0) return [];
    const security = await scanTabsSecurity(active);
    return buildSecurityAlertsFromTabs(active, security, {
      readIds: getSecurityAlertReadIds(),
      dismissedIds: getSecurityAlertDismissedIds(),
    });
  }, []);

  const buildLocalSecurityAlerts = useCallback(async () => {
    if (!sessionTabs?.length) {
      setSecurityAlerts([]);
      return;
    }
    setSecurityAlerts(await buildAlertsFromScans(sessionTabs));
  }, [sessionTabs, buildAlertsFromScans]);

  const fetchSecurityAlertsRemote = useCallback(async () => {
    if (!sessionId) {
      setSecurityAlerts([]);
      return;
    }
    try {
      const { tabs } = await tabService.getTabsBySession(sessionId, 1, 100);
      const active = tabs.filter((t) => !t.status || t.status === 'active');
      setSecurityAlerts(await buildAlertsFromScans(active));
    } catch (error) {
      console.error('Failed to build security alerts:', error);
      setSecurityAlerts([]);
    }
  }, [sessionId, buildAlertsFromScans]);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiData = await tabService.getNotifications().catch(() => [] as TabNotification[]);
      setNotifications(apiData.filter((n) => !isSecurityAlertId(n.id)));
      if (sessionTabs) {
        await buildLocalSecurityAlerts();
      } else {
        await fetchSecurityAlertsRemote();
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load alerts');
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionTabs, buildLocalSecurityAlerts, fetchSecurityAlertsRemote]);

  useEffect(() => {
    if (sessionTabs) {
      void buildLocalSecurityAlerts();
    }
  }, [sessionTabs, buildLocalSecurityAlerts, readRevision]);

  useEffect(() => {
    void fetchNotifications();
    const interval = setInterval(() => void fetchNotifications(), 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleWsNotification = (data: Record<string, unknown>) => {
      const newNotif: TabNotification = {
        id: String(data.id ?? ''),
        type: ['warning', 'success', 'error'].includes(String(data.type ?? '').toLowerCase())
          ? (String(data.type).toLowerCase() as TabNotification['type'])
          : 'info',
        title: String(data.title ?? ''),
        message: String(data.message ?? ''),
        tab: data.tab != null ? String(data.tab) : undefined,
        tabId: data.tab_id != null ? String(data.tab_id) : undefined,
        createdAt: String(data.created_at ?? new Date().toISOString()),
        read: Boolean(data.read),
      };
      setNotifications((prev) => [newNotif, ...prev]);
    };
    wsService.on('notification', handleWsNotification);
    return () => {
      wsService.off('notification', handleWsNotification);
    };
  }, []);

  const allNotifications = useMemo(() => {
    void readRevision;
    return [...securityAlerts, ...notifications].sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
    );
  }, [securityAlerts, notifications, readRevision]);

  const unreadCount = allNotifications.filter((n) => !n.read).length;

  useEffect(() => {
    if (unreadCount > 0) setExpanded(true);
  }, [unreadCount]);

  const markAsRead = async (id: string) => {
    try {
      if (isSecurityAlertId(id)) {
        markSecurityAlertRead(id);
        setSecurityAlerts((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
        setReadRevision((r) => r + 1);
        return;
      }
      await tabService.markNotificationRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      toast.error('Failed to mark alert as read');
    }
  };

  const markAllAsRead = async () => {
    if (unreadCount === 0) return;
    try {
      const securityIds = allNotifications.filter((n) => !n.read && isSecurityAlertId(n.id)).map((n) => n.id);
      if (securityIds.length > 0) {
        markAllSecurityAlertsRead(securityIds);
        setSecurityAlerts((prev) => prev.map((n) => ({ ...n, read: true })));
      }
      const apiUnread = allNotifications.some((n) => !n.read && !isSecurityAlertId(n.id));
      if (apiUnread) {
        await tabService.markAllNotificationsRead();
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      }
      setReadRevision((r) => r + 1);
      toast.success('All alerts marked as read');
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = (id: string) => {
    setSelectedNotificationId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteNotification = async () => {
    if (!selectedNotificationId) return;
    const id = selectedNotificationId;
    try {
      if (isSecurityAlertId(id)) {
        dismissSecurityAlert(id);
        setSecurityAlerts((prev) => prev.filter((n) => n.id !== id));
        setReadRevision((r) => r + 1);
      } else {
        await tabService.deleteNotification(id);
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }
      toast.success('Alert dismissed');
    } catch {
      toast.error('Failed to dismiss alert');
    } finally {
      setShowDeleteConfirm(false);
      setSelectedNotificationId(null);
    }
  };

  if (!isLoading && allNotifications.length === 0) {
    return null;
  }

  return (
    <div className={cn('mb-6', className)}>
      <Card className="overflow-hidden border border-khiimori-border/60">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-khiimori-bg-elevated/40 hover:bg-khiimori-bg-elevated/60 transition-colors text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-warning-500/10 shrink-0">
              <ShieldAlert className="w-5 h-5 text-warning-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-khiimori-text-primary">Security alerts</span>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning-500/20 text-warning-300">
                    {unreadCount} unread
                  </span>
                )}
                <span className="text-xs text-khiimori-text-muted">
                  {allNotifications.length} total
                </span>
              </div>
              {!expanded && unreadCount > 0 && (
                <p className="text-xs text-khiimori-text-muted truncate mt-0.5">
                  {allNotifications.find((n) => !n.read)?.title ?? allNotifications[0]?.title}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  void markAllAsRead();
                }}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark all read
              </Button>
            )}
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-khiimori-text-muted" />
            ) : (
              <ChevronDown className="w-5 h-5 text-khiimori-text-muted" />
            )}
          </div>
        </button>

        {expanded && (
          <div className="border-t border-khiimori-border/50 p-3 space-y-2 max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
              </div>
            ) : (
              allNotifications.map((notif) => (
                <div
                  key={notif.id}
                  className={cn(
                    'rounded-lg border border-khiimori-border/40 bg-khiimori-bg-secondary/60 p-3',
                    !notif.read && 'border-l-4 border-l-warning-500',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium uppercase', typeColor(notif.type))}>
                          {notif.type}
                        </span>
                        {isSecurityAlertId(notif.id) && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-violet-500/15 text-violet-300">
                            Security
                          </span>
                        )}
                        <h4 className="text-sm font-semibold text-khiimori-text-primary truncate">
                          {notif.title}
                        </h4>
                        {!notif.read && <span className="w-2 h-2 bg-info-500 rounded-full shrink-0" />}
                      </div>
                      {notif.message && (
                        <p className="text-xs text-khiimori-text-secondary mb-1.5 line-clamp-2">
                          {notif.message}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-[11px] text-khiimori-text-muted">
                        {notif.tab && <span className="truncate">Tab: {notif.tab}</span>}
                        {notif.tab && <span>·</span>}
                        <span className="flex items-center gap-1 shrink-0">
                          <Clock className="w-3 h-3" />
                          {formatRelative(notif.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {!notif.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void markAsRead(notif.id)}
                          aria-label="Mark as read"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteNotification(notif.id)}
                        aria-label="Dismiss alert"
                        className="text-khiimori-text-muted hover:text-error-400"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => void confirmDeleteNotification()}
        title="Dismiss alert"
        message="Remove this alert from your list?"
        confirmText="Dismiss"
        variant="danger"
      />
    </div>
  );
}
